import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Heading,
  Input,
  Switch,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  useToast,
  VStack,
  Card,
  CardHeader,
  CardBody,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import { useForm, Controller } from 'react-hook-form';
import { FiSave, FiAlertTriangle } from 'react-icons/fi';
import axios from 'axios';
import API from '../../config/api';
import * as settingsService from '../../services/api/settingsService';

/**
 * Settings component
 * Admin page for system configuration and settings
 */
const Settings = () => {
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [resetAlertOpen, setResetAlertOpen] = useState(false);
  const cancelRef = React.useRef();
  
  // Form state
  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm();
  
  // Initialize settings state
  const [settings, setSettings] = useState({
    general: {
      systemName: 'مجالسنا',
      contactEmail: 'info@majalisna.sa',
      supportPhone: '966138999999',
      logoUrl: ''
    },
    nomination: {
      isNominationOpen: false
    },
    notifications: {
      enableEmailNotifications: true,
      enableSMSNotifications: true,
      enableSMSAfterSubmit: false, // Default to false until we load from storage or API
      notificationTemplates: {
        applicationSubmitted: '',
        applicationApproved: '',
        applicationRejected: ''
      }
    }
  });

  // Fetch settings data
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Fetch nomination status
        const nominationResponse = await settingsService.getNominationStatus();
        
        // Update nomination status in settings
        setSettings(prev => ({
          ...prev,
          nomination: {
            isNominationOpen: nominationResponse.is_open
          }
        }));
        
        // Try to get SMS setting from local storage first
        const localSMSSetting = localStorage.getItem('enableSMSAfterSubmit');
        let smsAfterSubmitEnabled = localSMSSetting !== null ? localSMSSetting === 'true' : false;
        
        // Try to fetch from API if available
        try {
          const smsSettingResponse = await settingsService.getSettingByKey('enableSMSAfterSubmit');
          if (smsSettingResponse && smsSettingResponse.value !== undefined) {
            smsAfterSubmitEnabled = smsSettingResponse.value === true || smsSettingResponse.value === 'true';
            // Update local storage with the API value
            localStorage.setItem('enableSMSAfterSubmit', smsAfterSubmitEnabled.toString());
          }
        } catch (smsError) {
          console.log('SMS setting not found or API error, using local storage or default value');
        }
        
        // Update settings state with whatever value we have
        setSettings(prev => ({
          ...prev,
          notifications: {
            ...prev.notifications,
            enableSMSAfterSubmit: smsAfterSubmitEnabled
          }
        }));
        
        // Update form values
        setValue('notifications.enableSMSAfterSubmit', smsAfterSubmitEnabled);
        
      } catch (error) {
        console.error('Error fetching settings:', error);
        toast({
          title: 'خطأ في جلب البيانات',
          description: 'حدث خطأ أثناء جلب الإعدادات',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      }
    };

    fetchSettings();
  }, [toast, setValue]);
  
  // Handle nomination toggle
  const handleNominationToggle = async (isOpen) => {
    try {
      setIsSubmitting(true);
      
      await settingsService.setNominationStatus(isOpen);
      
      // Update nomination status only
      setSettings(prev => ({
        ...prev,
        nomination: {
          isNominationOpen: isOpen
        }
      }));
      
      toast({
        title: 'تم التحديث بنجاح',
        description: isOpen ? 'تم فتح باب الترشيح' : 'تم إغلاق باب الترشيح',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } catch (error) {
      console.error('Error updating nomination status:', error);
      toast({
        title: 'خطأ في التحديث',
        description: 'حدث خطأ أثناء تحديث حالة الترشيح',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle form submit
  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      
      // Save settings to backend
      await axios.post(API.admin.settings.update, data);
      
      // Save SMS settings specifically
      await settingsService.updateSetting('enableSMSAfterSubmit', {
        value: data.notifications.enableSMSAfterSubmit
      });
      
      toast({
        title: 'تم الحفظ بنجاح',
        description: 'تم حفظ الإعدادات بنجاح',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
      
      // Update local state
      setSettings(data);
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: 'خطأ في الحفظ',
        description: 'حدث خطأ أثناء حفظ الإعدادات',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle reset to defaults
  const handleResetToDefaults = async () => {
    try {
      setIsResetting(true);
      
      await axios.post(API.admin.settings.resetToDefaults);
      
      // Fetch updated settings
      const response = await axios.get(API.admin.settings.get);
      setSettings(response.data);
      
      // Reset form values
      reset(response.data);
      
      toast({
        title: 'تم إعادة التعيين بنجاح',
        description: 'تم إعادة تعيين الإعدادات إلى القيم الافتراضية بنجاح',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } catch (error) {
      toast({
        title: 'خطأ في إعادة التعيين',
        description: 'حدث خطأ أثناء إعادة تعيين الإعدادات',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsResetting(false);
      setResetAlertOpen(false);
    }
  };



  return (
    <Container maxW="container.xl" py={8}>
      <Box mb={8}>
        <Heading as="h1" size="lg" mb={2}>
          إعدادات النظام
        </Heading>
        <Text color="gray.600">
          تكوين وإدارة إعدادات نظام الترشيح
        </Text>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Tabs colorScheme="primary" variant="enclosed">
          <TabList>
            <Tab>الإعدادات العامة</Tab>
            <Tab>إعدادات الترشيح</Tab>
            <Tab>الإشعارات</Tab>
          </TabList>

          <TabPanels>
            {/* General Settings */}
            <TabPanel>
              <Card>
                <CardHeader>
                  <Heading size="md">الإعدادات العامة</Heading>
                </CardHeader>
                <CardBody>
                  <VStack spacing={6} align="stretch">
                    <FormControl>
                      <FormLabel>اسم النظام</FormLabel>
                      <Input
                        value="مجالسنا"
                        isReadOnly
                        bg="gray.100"
                      />
                      <FormHelperText>
                        اسم النظام ثابت ولا يمكن تغييره
                      </FormHelperText>
                    </FormControl>

                    <FormControl isInvalid={errors.general?.systemLogo}>
                      <FormLabel>شعار النظام</FormLabel>
                      <Input
                        type="file"
                        accept="image/*"
                        p={1}
                        {...register('general.systemLogo')}
                      />
                      {!errors.general?.systemLogo && (
                        <Text fontSize="sm" color="gray.600" mt={1}>
                          يفضل استخدام صورة بأبعاد 200×200 بكسل
                        </Text>
                      )}
                    </FormControl>

                    <FormControl isInvalid={errors.general?.contactEmail}>
                      <FormLabel>البريد الإلكتروني للتواصل</FormLabel>
                      <Input
                        type="email"
                        {...register('general.contactEmail', {
                          required: 'البريد الإلكتروني للتواصل مطلوب',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'البريد الإلكتروني غير صالح',
                          },
                        })}
                      />
                      <FormErrorMessage>
                        {errors.general?.contactEmail && errors.general.contactEmail.message}
                      </FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={errors.general?.supportPhone}>
                      <FormLabel>رقم هاتف الدعم</FormLabel>
                      <Input
                        {...register('general.supportPhone')}
                      />
                    </FormControl>
                  </VStack>
                </CardBody>
              </Card>
            </TabPanel>

            {/* Nomination Settings */}
            <TabPanel>
              <Card>
                <CardHeader>
                  <Heading size="md">إعدادات الترشيح</Heading>
                </CardHeader>
                <CardBody>
                  <VStack spacing={6} align="stretch">
                    <FormControl display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <FormLabel htmlFor="isNominationOpen" mb="1">
                          فتح باب الترشيح
                        </FormLabel>
                        <Text fontSize="sm" color="gray.600">
                          تحكم في إمكانية تقديم طلبات الترشيح الجديدة
                        </Text>
                      </Box>
                      <Switch
                        id="isNominationOpen"
                        colorScheme="green"
                        size="lg"
                        isChecked={settings.nomination?.isNominationOpen || false}
                        onChange={(e) => handleNominationToggle(e.target.checked)}
                      />
                    </FormControl>
                    
                    <Box p={4} bg={settings.nomination?.isNominationOpen ? 'green.50' : 'red.50'} borderRadius="md">
                      <Text fontSize="sm" color={settings.nomination?.isNominationOpen ? 'green.700' : 'red.700'}>
                        {settings.nomination?.isNominationOpen 
                          ? '✅ الترشيح مفتوح حالياً - يمكن للمستخدمين تقديم طلبات الترشيح'
                          : '❌ الترشيح مغلق حالياً - لا يمكن للمستخدمين تقديم طلبات جديدة'
                        }
                      </Text>
                    </Box>
                  </VStack>
                </CardBody>
              </Card>
            </TabPanel>



            {/* Notification Settings */}
            <TabPanel>
              <Card>
                <CardHeader>
                  <Heading size="md">إعدادات الإشعارات</Heading>
                </CardHeader>
                <CardBody>
                  <VStack spacing={6} align="stretch">
                    <FormControl display="flex" alignItems="center">
                      <FormLabel htmlFor="enableEmailNotifications" mb="0">
                        تفعيل إشعارات البريد الإلكتروني
                      </FormLabel>
                      <Controller
                        name="notifications.enableEmailNotifications"
                        control={control}
                        render={({ field: { onChange, value, ref } }) => (
                          <Switch
                            id="enableEmailNotifications"
                            colorScheme="green"
                            isChecked={value}
                            onChange={onChange}
                            ref={ref}
                          />
                        )}
                      />
                    </FormControl>

                    <FormControl display="flex" alignItems="center">
                      <FormLabel htmlFor="enableSMSNotifications" mb="0">
                        تفعيل إشعارات الرسائل النصية
                      </FormLabel>
                      <Controller
                        name="notifications.enableSMSNotifications"
                        control={control}
                        render={({ field: { onChange, value, ref } }) => (
                          <Switch
                            id="enableSMSNotifications"
                            colorScheme="green"
                            isChecked={value}
                            onChange={onChange}
                            ref={ref}
                          />
                        )}
                      />
                    </FormControl>

                    <FormControl display="flex" alignItems="center" pl={6} mt={-2}>
                      <FormLabel htmlFor="enableSMSAfterSubmit" mb="0">
                        إرسال رسالة نصية بعد تقديم الطلب
                      </FormLabel>
                      <Switch
                        id="enableSMSAfterSubmit"
                        colorScheme="green"
                        isChecked={settings.notifications?.enableSMSAfterSubmit === true}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          console.log('SMS toggle changed to:', checked);
                          
                          // Update local state immediately
                          setSettings(prev => ({
                            ...prev,
                            notifications: {
                              ...prev.notifications,
                              enableSMSAfterSubmit: checked
                            }
                          }));
                          
                          // Also update form value
                          setValue('notifications.enableSMSAfterSubmit', checked);
                          
                          // Always save to local storage for fallback
                          localStorage.setItem('enableSMSAfterSubmit', checked.toString());
                          
                          // Show success toast immediately
                          toast({
                            title: checked ? 'تم تفعيل الرسائل النصية' : 'تم تعطيل الرسائل النصية',
                            description: checked ? 'سيتم إرسال رسائل نصية بعد تقديم الطلبات' : 'لن يتم إرسال رسائل نصية بعد تقديم الطلبات',
                            status: 'success',
                            duration: 3000,
                            isClosable: true,
                            position: 'top',
                          });
                          
                          // Try direct API endpoint first
                          fetch('https://majalisna-61c919e79d71.herokuapp.com/api/v1/settings/enableSMSAfterSubmit', {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                            },
                            body: JSON.stringify({
                              value: checked
                            })
                          }).catch(error => {
                            console.log('Error updating SMS setting via direct API, using fallback', error);
                            
                            // Fallback to regular settings API
                            try {
                              settingsService.updateSetting('enableSMSAfterSubmit', {
                                value: checked.toString()
                              }).catch(updateError => {
                                console.error('Error updating SMS setting, trying to create it:', updateError);
                                
                                // If update fails, try to create the setting
                                settingsService.createSetting({
                                  key: 'enableSMSAfterSubmit',
                                  value: checked.toString(),
                                  description: 'Enable SMS notifications after form submission'
                                }).catch(createError => {
                                  console.error('Error creating SMS setting:', createError);
                                });
                              });
                            } catch (serviceError) {
                              console.error('Error with SMS setting service:', serviceError);
                            }
                          });
                        }}
                      isDisabled={!settings.notifications?.enableSMSNotifications}
                    />
                  </FormControl>
                  
                  {settings.notifications?.enableSMSNotifications && (
                    <Box p={3} bg="blue.50" borderRadius="md" fontSize="sm">
                      <Text fontWeight="medium">معلومات خدمة الرسائل النصية</Text>
                      <Text mt={1}>يتم استخدام خدمة الرسائل النصية لإرسال إشعارات مهمة للمستخدمين مثل تأكيد تقديم الطلب وحالة الطلب.</Text>
                      {settings.notifications?.enableSMSAfterSubmit ? (
                        <Text mt={1} fontWeight="bold" color="green.600">
                          ✅ تم تفعيل إرسال الرسائل النصية بعد تقديم الطلب
                        </Text>
                      ) : (
                        <Text mt={1} fontWeight="bold" color="red.600">
                          ❌ تم تعطيل إرسال الرسائل النصية بعد تقديم الطلب
                        </Text>
                      )}
                    </Box>
                  )}

                    <FormControl isInvalid={errors.notifications?.adminNotificationEmails}>
                      <FormLabel>بريد إشعارات المدير</FormLabel>
                      <Input
                        {...register('notifications.adminNotificationEmails', {
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'البريد الإلكتروني غير صالح',
                          },
                        })}
                      />
                      {!errors.notifications?.adminNotificationEmails && (
                        <Text fontSize="sm" color="gray.600" mt={1}>
                          يمكن إدخال عدة عناوين بريد إلكتروني مفصولة بفواصل
                        </Text>
                      )}
                    </FormControl>

                    <Divider />

                    <Heading as="h4" size="sm">
                      قوالب الإشعارات
                    </Heading>

                    <FormControl isInvalid={errors.notifications?.notificationTemplates?.applicationSubmitted}>
                      <FormLabel>إشعار تقديم الطلب</FormLabel>
                      <Textarea
                        {...register('notifications.notificationTemplates.applicationSubmitted')}
                        rows={3}
                      />
                    </FormControl>

                    <FormControl isInvalid={errors.notifications?.notificationTemplates?.applicationApproved}>
                      <FormLabel>إشعار قبول الطلب</FormLabel>
                      <Textarea
                        {...register('notifications.notificationTemplates.applicationApproved')}
                        rows={3}
                      />
                    </FormControl>

                    <FormControl isInvalid={errors.notifications?.notificationTemplates?.applicationRejected}>
                      <FormLabel>إشعار رفض الطلب</FormLabel>
                      <Textarea
                        {...register('notifications.notificationTemplates.applicationRejected')}
                        rows={3}
                      />
                    </FormControl>
                  </VStack>
                </CardBody>
              </Card>
            </TabPanel>


          </TabPanels>
        </Tabs>

        {/* Action Buttons */}
        <Flex justify="space-between" mt={8}>
          <Button
            leftIcon={<FiAlertTriangle />}
            colorScheme="red"
            variant="outline"
            onClick={() => setResetAlertOpen(true)}
          >
            إعادة التعيين إلى الإعدادات الافتراضية
          </Button>
          
          <Button
            leftIcon={<FiSave />}
            colorScheme="primary"
            type="submit"
            isLoading={isSubmitting}
            loadingText="جاري الحفظ..."
          >
            حفظ الإعدادات
          </Button>
        </Flex>
      </form>

      {/* Reset Confirmation Dialog */}
      <AlertDialog
        isOpen={resetAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setResetAlertOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              إعادة تعيين الإعدادات
            </AlertDialogHeader>

            <AlertDialogBody>
              هل أنت متأكد من رغبتك في إعادة تعيين جميع الإعدادات إلى القيم الافتراضية؟
              <Text color="red.500" mt={2}>
                هذا الإجراء لا يمكن التراجع عنه.
              </Text>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setResetAlertOpen(false)}>
                إلغاء
              </Button>
              <Button
                colorScheme="red"
                onClick={handleResetToDefaults}
                isLoading={isResetting}
                ml={3}
              >
                إعادة التعيين
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Container>
  );
};

export default Settings;
